/**
 * Apply composed VO current-scope onto D-Tools staging lines for live proposal render.
 * Read-time only — does not mutate stored proposal_items.
 */

import type { DtoolsStagingLine } from "./dtoolsJsonStaging";
import type { DtoolsReviseLine } from "./dtoolsProposalRevise";
import type { ComposedProjectScope, ComposedScopeLine, ScopeAnnotation } from "./variationOrderCurrentScope";
import { stableLineKeyFromDtoolsReviseLine } from "./variationOrderDtoolsMap";
import {
  enrichVoPlacement,
  hierarchyLocationIdForPlacement,
  resolveVoRoomDisplayName,
  resolveVoSystemDisplayName,
} from "./variationOrderPlacement";
import { coalesceNonEmpty, coalescePositiveId } from "./variationOrderCataloguePresentation";
import { roundVoMoney } from "./variationOrderTypes";
import {
  renderVoAnnotationBadgeText,
  renderVoAnnotationChangeReason,
  renderVoAnnotationDate,
  renderVoAnnotationStatusBadge,
} from "./rendering/voAnnotation";

export type VoLiveAnnotation = ScopeAnnotation & {
  kind: "added" | "removed" | "quantity_changed";
};

export type DtoolsStagingLineWithVo = DtoolsStagingLine & {
  variationOrderAnnotation?: VoLiveAnnotation;
};

function stagingStableKey(line: DtoolsStagingLine): string {
  return stableLineKeyFromDtoolsReviseLine(line as DtoolsReviseLine);
}

function annotationForLine(line: ComposedScopeLine): VoLiveAnnotation | undefined {
  if (line.addedBy) return { ...line.addedBy, kind: "added" };
  if (line.quantityChangedBy) return { ...line.quantityChangedBy, kind: "quantity_changed" };
  return undefined;
}

function resolveParentStagingId(
  parentStableLineKey: string | null | undefined,
  keyToStagingId: Map<string, string>,
): string | null {
  const key = String(parentStableLineKey ?? "").trim();
  if (!key) return null;
  return keyToStagingId.get(key) ?? null;
}

/** Prefer existing non-empty presentation; fill gaps from composed VO scope. */
function mergePresentationIntoStaging(
  base: DtoolsStagingLine,
  line: ComposedScopeLine,
): DtoolsStagingLine {
  const brand = coalesceNonEmpty(base.manufacturer, line.brand);
  const model = coalesceNonEmpty(base.model, line.model);
  const displayName = coalesceNonEmpty(base.displayName, line.displayName, line.name);
  const polished = coalesceNonEmpty(
    base.proposalDescriptionOverride,
    base.polishedDescription,
    base.cleoDescription,
    line.description,
    base.dtoolsDescription,
  );
  const dtoolsDesc = coalesceNonEmpty(base.dtoolsDescription, line.description, line.name) ?? "";
  const imageUrl = coalesceNonEmpty(base.proposalImageOverride, base.imageUrl, line.imageUrl);
  return {
    ...base,
    manufacturer: brand,
    model,
    displayName,
    polishedDescription: polished ?? base.polishedDescription ?? "",
    dtoolsDescription: dtoolsDesc,
    imageUrl,
    catalogProductId: coalescePositiveId(base.catalogProductId, line.catalogProductId),
    projectCatalogueId: coalescePositiveId(base.projectCatalogueId, line.projectCatalogueId),
    catalogueKey: coalesceNonEmpty(base.catalogueKey, line.catalogueKey) ?? base.catalogueKey ?? "",
    customerExplanation: coalesceNonEmpty(base.customerExplanation, line.customerExplanation),
  };
}

function synthesizeStagingFromComposed(
  line: ComposedScopeLine,
  sortOrder: number,
  ann: VoLiveAnnotation | undefined,
  keyToStagingId: Map<string, string>,
): DtoolsStagingLineWithVo {
  const dtoolsId = line.stableLineKey.startsWith("dtools:")
    ? line.stableLineKey.slice("dtools:".length)
    : line.stableLineKey;
  const placement = enrichVoPlacement(line);
  const systemName = resolveVoSystemDisplayName(placement);
  const roomName = resolveVoRoomDisplayName(placement);
  const locationId = hierarchyLocationIdForPlacement(placement);
  const parentId = resolveParentStagingId(line.parentStableLineKey, keyToStagingId);
  const isAccessory = Boolean(line.isAccessory || parentId);
  const id = `vo-scope:${line.stableLineKey}${ann?.kind === "removed" ? ":removed" : ""}`;
  keyToStagingId.set(line.stableLineKey, id);
  const description = coalesceNonEmpty(line.description, line.name) ?? "";
  const displayName = coalesceNonEmpty(line.displayName, line.name);
  return {
    id,
    dtoolsItemId: dtoolsId,
    manufacturer: line.brand,
    model: line.model,
    displayName,
    category: null,
    subcategory: null,
    dtoolsDescription: description,
    polishedDescription: coalesceNonEmpty(line.description) ?? "",
    quantity: line.quantity,
    unitPrice: line.unitPrice,
    lineTotal: line.lineTotal,
    systemId: placement.systemId,
    systemName,
    locationId,
    roomName: roomName === "Room not available" ? null : roomName,
    parentId,
    sortOrder,
    bucket: "commercial",
    imageUrl: line.imageUrl ?? null,
    projectCatalogueId: line.projectCatalogueId ?? null,
    catalogueKey: line.catalogueKey ?? "",
    catalogId: line.catalogProductId != null ? String(line.catalogProductId) : null,
    componentId: null,
    isAccessory,
    catalogProductId: line.catalogProductId ?? null,
    catalogMatchConfidence:
      line.catalogProductId || line.projectCatalogueId ? "matched" : null,
    customerExplanation: line.customerExplanation ?? null,
    mergedSources: [
      {
        dtoolsItemId: dtoolsId,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        lineTotal: line.lineTotal,
        dtoolsDescription: description,
      },
    ],
    variationOrderAnnotation: ann,
  };
}

/**
 * Rebuild staging lines from composed current scope so removals stay inline under
 * their original System → Room → Primary/Supporting grouping, quantities reflect
 * approved VOs, and adds appear with VO badges.
 */
export function applyComposedScopeToStagingLines(
  stagingLines: DtoolsStagingLine[],
  scope: ComposedProjectScope,
): DtoolsStagingLineWithVo[] {
  const byKey = new Map<string, DtoolsStagingLine>();
  const keyToStagingId = new Map<string, string>();
  for (const line of stagingLines) {
    const key = stagingStableKey(line);
    byKey.set(key, line);
    keyToStagingId.set(key, line.id);
  }

  const out: DtoolsStagingLineWithVo[] = [];

  // First pass: active current-scope lines (preserve live parent ids).
  scope.currentLines.forEach((current, idx) => {
    const base = byKey.get(current.stableLineKey);
    const ann = annotationForLine(current);
    if (base) {
      const quantity = current.quantity;
      const unitPrice = current.unitPrice;
      const lineTotal = roundVoMoney(current.lineTotal ?? unitPrice * quantity);
      const placement = enrichVoPlacement(current);
      const parentId =
        resolveParentStagingId(current.parentStableLineKey, keyToStagingId) ?? base.parentId ?? null;
      const merged = mergePresentationIntoStaging(base, current);
      const row: DtoolsStagingLineWithVo = {
        ...merged,
        quantity,
        unitPrice,
        lineTotal,
        systemId: placement.systemId ?? base.systemId,
        systemName: resolveVoSystemDisplayName(placement) || base.systemName,
        locationId: hierarchyLocationIdForPlacement(placement) ?? base.locationId,
        roomName:
          (resolveVoRoomDisplayName(placement) !== "Room not available"
            ? resolveVoRoomDisplayName(placement)
            : null) ?? base.roomName,
        parentId,
        isAccessory: Boolean(current.isAccessory || parentId || base.isAccessory),
        variationOrderAnnotation: ann,
      };
      keyToStagingId.set(current.stableLineKey, row.id);
      out.push(row);
      return;
    }
    out.push(synthesizeStagingFromComposed(current, idx, ann, keyToStagingId));
  });

  // Second pass: historical removals — keep original staging id so Supporting nesting stays valid.
  scope.previousItems.forEach((prev, idx) => {
    const removedAnn: VoLiveAnnotation = { ...prev.removedBy, kind: "removed" };
    const base = byKey.get(prev.stableLineKey);
    if (base) {
      const placement = enrichVoPlacement(prev);
      const parentId =
        resolveParentStagingId(prev.parentStableLineKey, keyToStagingId) ?? base.parentId ?? null;
      const merged = mergePresentationIntoStaging(base, prev);
      // Keep base.id so accessories of a removed primary (and this accessory under a live primary) nest correctly.
      const row: DtoolsStagingLineWithVo = {
        ...merged,
        quantity: prev.quantity,
        unitPrice: prev.unitPrice,
        lineTotal: prev.lineTotal,
        systemId: placement.systemId ?? base.systemId,
        systemName: resolveVoSystemDisplayName(placement) || base.systemName,
        locationId: hierarchyLocationIdForPlacement(placement) ?? base.locationId,
        roomName:
          (resolveVoRoomDisplayName(placement) !== "Room not available"
            ? resolveVoRoomDisplayName(placement)
            : null) ?? base.roomName,
        parentId,
        isAccessory: Boolean(prev.isAccessory || parentId || base.isAccessory),
        variationOrderAnnotation: removedAnn,
      };
      keyToStagingId.set(prev.stableLineKey, row.id);
      out.push(row);
      return;
    }
    out.push(
      synthesizeStagingFromComposed(prev, scope.currentLines.length + idx, removedAnn, keyToStagingId),
    );
  });

  // Third pass: re-bind parentId now that all ids (including synthesized parents) are known.
  return out.map((line) => {
    const scopeLine =
      scope.currentLines.find((l) => l.stableLineKey === stagingStableKey(line)) ??
      scope.previousItems.find((l) => l.stableLineKey === stagingStableKey(line));
    const parentKey = scopeLine?.parentStableLineKey;
    if (!parentKey) return line;
    const parentId = resolveParentStagingId(parentKey, keyToStagingId);
    if (!parentId || parentId === line.parentId) return line;
    return { ...line, parentId, isAccessory: true };
  });
}

export function voAnnotationBadgeText(ann: VoLiveAnnotation | undefined): string | null {
  return renderVoAnnotationBadgeText(ann);
}

export function voAnnotationStatusBadge(ann: VoLiveAnnotation | undefined): string | null {
  return renderVoAnnotationStatusBadge(ann);
}

/** @deprecated Prefer renderVoAnnotationDate / renderVoAnnotationMetaLine from shared/rendering. */
export function voAnnotationMetaText(ann: VoLiveAnnotation | undefined): string | null {
  return renderVoAnnotationDate(ann?.date);
}

export function voAnnotationChangeReason(ann: VoLiveAnnotation | undefined): string | null {
  return renderVoAnnotationChangeReason(ann);
}

export function voStaffPreviewPath(ann: VoLiveAnnotation | undefined): string | null {
  return ann ? `/sales/vo/${ann.voId}/preview` : null;
}
